'use client'

import { forwardRef, useCallback, useMemo } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'

import { setDateByType } from '@/helpers/date'

export interface HourSelect {
  value: Date | undefined
  setValue: (value: Date) => void
}

export const HourSelect = forwardRef<HTMLButtonElement, HourSelect>(
  ({ value, setValue }, ref) => {
    const handleValueChange = useCallback(
      (hour: string) => {
        const baseDate = value ? new Date(value) : new Date()
        const currentHour24 = value ? value.getHours() : 0
        const isCurrentPM = currentHour24 >= 12
        const selectedHour12 = Number(hour)

        // Map 12-hour selection to 24-hour based on current period
        // PM: 1-11 -> 13-23, 12 -> 12
        // AM: 1-11 -> 1-11, 12 -> 0
        const mappedHour24 = isCurrentPM
          ? (selectedHour12 % 12) + 12
          : selectedHour12 % 12 === 0
            ? 0
            : selectedHour12

        setValue(setDateByType(baseDate, mappedHour24.toString(), 'hours'))
      },
      [value, setValue],
    )

    const hour = useMemo(() => {
      const hours = value?.getHours() ?? 12
      if (hours === 0) {
        return '12'
      }
      if (hours > 12) {
        return (hours - 12).toString()
      }
      return hours.toString()
    }, [value])

    return (
      <div className="flex items-center">
        <Select value={hour} onValueChange={handleValueChange}>
          <SelectTrigger ref={ref} className="w-full h-8">
            <SelectValue>{hour}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {[...Array(12)].map((_, i) => {
              const hour = i + 1
              return (
                <SelectItem key={i} value={hour.toString()}>
                  {hour.toString()}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>
    )
  },
)

HourSelect.displayName = 'HourSelect'
